import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { AnnouncementWidgetComponent } from '../../../shared/components/announcement-widget/announcement-widget.component';
import { PulseWidgetComponent } from '../../../shared/components/pulse-widget/pulse-widget.component';

@Component({
  selector: 'app-manager-dashboard',
  templateUrl: './manager-dashboard.component.html',
  styleUrls: ['./manager-dashboard.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, AnnouncementWidgetComponent, PulseWidgetComponent]
})
export class ManagerDashboardComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  loading = true;
  currentUser: any = null;

  teamSize      = 0;
  presentToday  = 0;
  absentToday   = 0;
  lateToday     = 0;
  onLeaveToday  = 0;
  wfhToday      = 0;

  liveOnTime:    any[] = [];
  liveLate:      any[] = [];
  liveOnLeave:   any[] = [];
  liveAbsentees: any[] = [];

  pendingLeaveApprovals: any[] = [];

  deptStats: { name: string; present: number; total: number; pct: number; color: string }[] = [];

  private readonly CIRC = 301.6;
  private readonly DEPT_COLORS = ['#8b1a1a', '#2563eb', '#fea940', '#16a34a', '#7c3aed'];

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router
  ) {}

  viewAllAttendance(): void {
    this.router.navigate(['/attendance']);
  }

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.loadManagerData();
  }

  loadManagerData(): void {
    this.loading = true;

    this.apiService.get('/employees/my-team').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const teamMembers = Array.isArray(res) ? res : (res?.data ?? []);
        this.teamSize = teamMembers.length;

        const today = this.localDateStr(new Date());
        this.apiService.get(`/attendance?date=${today}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (attendance: any[]) => {
            this.processAttendanceData(teamMembers, attendance);
            this.buildDeptStats(teamMembers, attendance);
            this.loading = false;
          },
          error: () => { this.loading = false; }
        });

        this.loadPendingLeaves();
      },
      error: () => { this.loading = false; }
    });
  }

  processAttendanceData(teamMembers: any[], attendance: any[]): void {
    this.presentToday = 0; this.absentToday = 0;
    this.lateToday = 0;   this.onLeaveToday = 0; this.wfhToday = 0;
    this.liveOnTime = []; this.liveLate = []; this.liveOnLeave = []; this.liveAbsentees = [];

    teamMembers.forEach(emp => {
      const att = attendance.find((a: any) => a.employeeId === emp.id);

      if (!att || att.status === 'absent') {
        this.absentToday++;
        this.liveAbsentees.push({ ...emp, attStatus: 'absent' });
      } else if (att.status === 'on-leave') {
        this.onLeaveToday++;
        this.liveOnLeave.push({ ...emp, attStatus: 'on-leave' });
      } else if (att.status === 'wfh' || att.status === 'work-from-home') {
        this.wfhToday++; this.presentToday++;
        this.liveOnTime.push({ ...emp, checkIn: att.checkIn, attStatus: 'wfh' });
      } else if (att.status === 'late') {
        this.lateToday++; this.presentToday++;
        this.liveLate.push({ ...emp, checkIn: att.checkIn, attStatus: 'late' });
      } else if (att.status === 'present') {
        this.presentToday++;
        this.liveOnTime.push({ ...emp, checkIn: att.checkIn, attStatus: 'present' });
      }
    });
  }

  buildDeptStats(teamMembers: any[], attendance: any[]): void {
    const deptMap: Record<string, { present: number; total: number }> = {};

    teamMembers.forEach(emp => {
      const dept = emp.department || emp.departmentName || 'Other';
      if (!deptMap[dept]) deptMap[dept] = { present: 0, total: 0 };
      deptMap[dept].total++;
      const att = attendance.find((a: any) => a.employeeId === emp.id);
      if (att && (att.status === 'present' || att.status === 'late' || att.status === 'wfh')) {
        deptMap[dept].present++;
      }
    });

    this.deptStats = Object.entries(deptMap)
      .map(([name, v], i) => ({
        name,
        present: v.present,
        total: v.total,
        pct: v.total > 0 ? Math.round((v.present / v.total) * 100) : 0,
        color: this.DEPT_COLORS[i % this.DEPT_COLORS.length]
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 4);
  }

  loadPendingLeaves(): void {
    this.apiService.get('/leaves').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const leaves = Array.isArray(res) ? res : (res?.data ?? []);
        this.pendingLeaveApprovals = leaves.filter((l: any) => l.status === 'pending').slice(0, 4);
      },
      error: () => {}
    });
  }

  approveLeave(leaveId: string): void {
    this.apiService.patch(`/leaves/${leaveId}`, { status: 'approved' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadPendingLeaves(),
      error: () => {}
    });
  }

  rejectLeave(leaveId: string): void {
    this.apiService.patch(`/leaves/${leaveId}`, { status: 'rejected' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadPendingLeaves(),
      error: () => {}
    });
  }

  arcLen(count: number, total: number): number {
    return total ? (count / total) * this.CIRC : 0;
  }

  arcOffset(priorCount: number, total: number): number {
    return total ? 75.4 - (priorCount / total) * this.CIRC : 75.4;
  }

  get attendancePct(): number {
    return this.teamSize > 0 ? Math.round((this.presentToday / this.teamSize) * 100) : 0;
  }

  getInitials(firstName: string, lastName: string): string {
    return ((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase();
  }

  getHoursWorked(checkIn: string): string {
    if (!checkIn) return '—';
    try {
      const parts = checkIn.split(' ');
      const [h, m] = parts[0].split(':').map(Number);
      const meridiem = parts[1];
      let hours = h;
      if (meridiem === 'PM' && h !== 12) hours += 12;
      if (meridiem === 'AM' && h === 12) hours = 0;
      const cin = new Date();
      cin.setHours(hours, m, 0, 0);
      const diff = (Date.now() - cin.getTime()) / 3600000;
      return diff < 0 ? '—' : `${diff.toFixed(1)}h`;
    } catch { return '—'; }
  }

  fmtDate(d: string): string {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private localDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
